import type { ReactNode } from "react"
import {
  ActivityIcon,
  CakeIcon,
  InboxIcon,
  ReceiptIcon,
  WalletIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  BarChart3Icon,
  CalendarCheckIcon,
  CheckCircle2Icon,
  CircleUserRoundIcon,
  ClipboardCheckIcon,
  FileClockIcon,
  LineChartIcon,
  PieChartIcon,
  ScaleIcon,
  UserPlusIcon,
  UserRoundPlusIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react"

import type { useTranslations } from "@/i18n"
import type { DashboardOverviewMode } from "@/features/dashboard/hooks/use-dashboard-page-state"
import type { DashboardData } from "@/features/dashboard/model/dashboard-types"
import {
  DashboardWidgetChrome,
  type DashboardLayout,
  type DashboardWidget,
  type DashboardWidgetRegistry,
  type DashboardWidgetSpan,
} from "@ibada/dashboard-engine"
import {
  BRANCH_DASHBOARD_WIDGET_SEEDS,
  BRANCH_HOME_D4_WIDGET_SEEDS,
  DashboardBoardVariant,
  DashboardWidgetId,
  PARISH_DASHBOARD_WIDGET_SEEDS,
  PARISH_HOME_D4_WIDGET_SEEDS,
} from "@/features/dashboard/overview/lib/dashboard-widget-ids"
import type { MembersBoardData } from "@/features/members/components/board/members-board-data"
import {
  BoardBranchesWidget,
  BoardIntegrationWidget,
  BoardMembersWidget,
  BoardMixWidget,
  BoardNewMembersWidget,
  BoardToConfirmWidget,
  BoardVisitorsWidget,
} from "@/features/members/components/board/members-board-widgets"
import {
  HomeApprovalsWidget,
  HomeBirthdaysWidget,
  HomeBudgetWidget,
  HomeMembersWidget,
  HomeNewMembersWidget,
  HomeRecentVisitorsWidget,
  HomeSpendingWidget,
  HomeTreasuryWidget,
} from "@/features/dashboard/home-d4/components/home-d4-widgets"
import type { HomeD4Data } from "@/features/dashboard/home-d4/hooks/use-home-d4-data"
import { HomeTileId, HomeTileState } from "@/features/dashboard/home-d4/lib/home-grid-layout"
import {
  DashboardActiveActivitiesWidget,
  DashboardActiveMembersWidget,
  DashboardFinanceTrendWidget,
  DashboardMemberGrowthWidget,
  DashboardMemberMixWidget,
  DashboardNetBalanceWidget,
  DashboardNewMembersWidget,
  DashboardParishAgeInsightsWidget,
  DashboardParishBranchDistributionWidget,
  DashboardParishGenderInsightsWidget,
  DashboardPendingApprovalsWidget,
  DashboardRecentRecordsWidget,
  DashboardScopeNoteWidget,
  DashboardTotalExpensesWidget,
  DashboardTotalIncomeWidget,
  DashboardTotalMembersWidget,
  DashboardTransactionCountWidget,
  DashboardVisitorsWidget,
  DashboardTransfersOutWidget,
  DashboardCashOnHandWidget,
} from "@/features/dashboard/overview/widgets/dashboard-overview-widgets"
import { chartHasRenderableData } from "@/features/finance/lib/finance-overview-integration"
import {
  findDashboardRecentRecordsTable,
  getDashboardRecentRecordKind,
  hasDashboardRecentRecords,
} from "@/features/dashboard/components/dashboard-recent-records-card"
import { FinanceOverviewCardId } from "@/features/finance/model/finance-normalized-overview"
import { OverviewContext } from "@ibada/shared-types/overview"

type Translate = ReturnType<typeof useTranslations>

interface DashboardWidgetDef {
  id: DashboardWidgetId
  i18nKey: string
  icon: LucideIcon
  maxSpan: DashboardWidgetSpan
  /** Chart/dense widgets open full-size in a dialog; compact KPI tiles don't. */
  expandable?: boolean
  /** The D4 tiles draw their own card; everything else uses the engine's. */
  chrome?: DashboardWidgetChrome
  Component: () => ReactNode
  isVisible: (input: DashboardWidgetRegistryInput) => boolean
  title?: (t: Translate, input: DashboardWidgetRegistryInput) => string
  subtitle?: (t: Translate, input: DashboardWidgetRegistryInput) => string
}

export interface DashboardWidgetRegistryInput {
  data: DashboardData
  overviewMode: DashboardOverviewMode
  /** Defaults from `overviewMode`: parish, else the legacy branch board (Branch Details). */
  variant?: DashboardBoardVariant
  /** The D4 tiles' data and states; only the branch home passes it. */
  home?: HomeD4Data
  /**
   * The members board, fetched for the active context: the parish board (D4 parish tiles) or the branch home
   * (its who-we-are tile). The Branch Details console never passes it.
   */
  membersBoard?: MembersBoardData
}

export function boardVariantOf(input: DashboardWidgetRegistryInput): DashboardBoardVariant {
  if (input.variant) return input.variant
  return input.overviewMode === "parish" ? DashboardBoardVariant.Parish : DashboardBoardVariant.BranchDetails
}

/** A D4 tile is on the board only on the branch home, and only while the reader may see it (see `HomeD4Data`). */
function homeTileVisible(id: HomeTileId) {
  return (input: DashboardWidgetRegistryInput) =>
    boardVariantOf(input) === DashboardBoardVariant.BranchHome &&
    Boolean(input.home) &&
    input.home?.states[id] !== HomeTileState.Absent
}

/** Whether the members board was passed and is readable (loading or ready). */
function membersBoardReadable(input: DashboardWidgetRegistryInput): boolean {
  return Boolean(input.membersBoard) && input.membersBoard?.state !== HomeTileState.Absent
}

/** A parish D4 tile is on the board only on the parish home, while its members board is readable. */
function parishTileVisible(input: DashboardWidgetRegistryInput): boolean {
  return boardVariantOf(input) === DashboardBoardVariant.Parish && membersBoardReadable(input)
}

/** A members board tile on the branch home: only there, while the branch's members board is readable. */
function branchHomeBoardTileVisible(input: DashboardWidgetRegistryInput): boolean {
  return boardVariantOf(input) === DashboardBoardVariant.BranchHome && membersBoardReadable(input)
}

/** Parish D4 spans: members per branch and who we are are the two wide tiles. */
const PARISH_HOME_D4_SPANS: Record<string, DashboardWidgetSpan> = {
  [DashboardWidgetId.ParishBranches]: 2,
  [DashboardWidgetId.ParishMix]: 2,
}

/** Default column spans of the D4 arrangement (3 tracks): treasury is the one wide tile. */
const BRANCH_HOME_D4_SPANS: Record<string, DashboardWidgetSpan> = {
  [DashboardWidgetId.HomeTreasury]: 2,
  [DashboardWidgetId.HomeMix]: 2,
}

function isParishOverview({ data, overviewMode }: DashboardWidgetRegistryInput): boolean {
  return overviewMode === "parish" || data.mergedOverview?.meta.context === OverviewContext.Parish
}

function hasFinanceStat(input: DashboardWidgetRegistryInput, key: FinanceOverviewCardId): boolean {
  return (
    input.overviewMode === "branch" &&
    input.data.visibility.showKpis &&
    input.data.overviewLayout.financeSummary.some((stat) => stat.key === key)
  )
}

const HOME_D4_WIDGETS: readonly DashboardWidgetDef[] = [
  {
    id: DashboardWidgetId.HomeTreasury,
    i18nKey: "homeTreasury",
    icon: WalletIcon,
    maxSpan: 3,
    chrome: DashboardWidgetChrome.None,
    Component: HomeTreasuryWidget,
    isVisible: homeTileVisible(HomeTileId.Treasury),
  },
  {
    id: DashboardWidgetId.HomeApprovals,
    i18nKey: "homeApprovals",
    icon: InboxIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeApprovalsWidget,
    isVisible: homeTileVisible(HomeTileId.Approvals),
  },
  {
    id: DashboardWidgetId.HomeMembers,
    i18nKey: "homeMembers",
    icon: UsersIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeMembersWidget,
    isVisible: homeTileVisible(HomeTileId.Members),
  },
  {
    id: DashboardWidgetId.HomeRecentVisitors,
    i18nKey: "homeRecentVisitors",
    icon: UserRoundPlusIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeRecentVisitorsWidget,
    isVisible: homeTileVisible(HomeTileId.RecentVisitors),
  },
  {
    id: DashboardWidgetId.HomeBirthdays,
    i18nKey: "homeBirthdays",
    icon: CakeIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeBirthdaysWidget,
    isVisible: homeTileVisible(HomeTileId.Birthdays),
  },
  {
    id: DashboardWidgetId.HomeNewMembers,
    i18nKey: "homeNewMembers",
    icon: UserPlusIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeNewMembersWidget,
    isVisible: homeTileVisible(HomeTileId.NewMembers),
  },
  {
    id: DashboardWidgetId.HomeBudget,
    i18nKey: "homeBudget",
    icon: PieChartIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeBudgetWidget,
    isVisible: homeTileVisible(HomeTileId.Budget),
  },
  {
    id: DashboardWidgetId.HomeSpending,
    i18nKey: "homeSpending",
    icon: ReceiptIcon,
    maxSpan: 2,
    chrome: DashboardWidgetChrome.None,
    Component: HomeSpendingWidget,
    isVisible: homeTileVisible(HomeTileId.Spending),
  },
  {
    // The same tile as the parish home's who we are: the members board serves the branch scope here.
    id: DashboardWidgetId.HomeMix,
    i18nKey: "homeMix",
    icon: CircleUserRoundIcon,
    maxSpan: 3,
    chrome: DashboardWidgetChrome.None,
    Component: BoardMixWidget,
    isVisible: branchHomeBoardTileVisible,
  },
]

const PARISH_D4_WIDGETS: readonly DashboardWidgetDef[] = (
  [
  { id: DashboardWidgetId.ParishMembers, i18nKey: "parishMembers", icon: UsersIcon, maxSpan: 2, Component: BoardMembersWidget },
  { id: DashboardWidgetId.ParishBranches, i18nKey: "parishBranches", icon: BarChart3Icon, maxSpan: 3, Component: BoardBranchesWidget },
  { id: DashboardWidgetId.ParishNewMembers, i18nKey: "parishNewMembers", icon: UserPlusIcon, maxSpan: 2, Component: BoardNewMembersWidget },
  { id: DashboardWidgetId.ParishVisitors, i18nKey: "parishVisitors", icon: UserRoundPlusIcon, maxSpan: 2, Component: BoardVisitorsWidget },
  { id: DashboardWidgetId.ParishToConfirm, i18nKey: "parishToConfirm", icon: ClipboardCheckIcon, maxSpan: 2, Component: BoardToConfirmWidget },
  { id: DashboardWidgetId.ParishMix, i18nKey: "parishMix", icon: CircleUserRoundIcon, maxSpan: 3, Component: BoardMixWidget },
  { id: DashboardWidgetId.ParishIntegration, i18nKey: "parishIntegration", icon: UsersIcon, maxSpan: 2, Component: BoardIntegrationWidget },
  ] satisfies Array<Pick<DashboardWidgetDef, "id" | "i18nKey" | "icon" | "maxSpan" | "Component">>
).map((widget) => ({ ...widget, chrome: DashboardWidgetChrome.None, isVisible: parishTileVisible }))

const DASHBOARD_WIDGETS: readonly DashboardWidgetDef[] = [
  ...HOME_D4_WIDGETS,
  ...PARISH_D4_WIDGETS,
  {
    id: DashboardWidgetId.TotalMembers,
    i18nKey: "totalMembers",
    icon: UsersIcon,
    maxSpan: 1,
    Component: DashboardTotalMembersWidget,
    isVisible: ({ data }) => data.visibility.showKpis && Boolean(data.overviewLayout.memberStats.totalMembers),
  },
  {
    id: DashboardWidgetId.ActiveMembers,
    i18nKey: "activeMembers",
    icon: CheckCircle2Icon,
    maxSpan: 1,
    Component: DashboardActiveMembersWidget,
    isVisible: ({ data }) => data.visibility.showKpis && Boolean(data.overviewLayout.memberStats.activeMembers),
  },
  {
    id: DashboardWidgetId.NewMembers,
    i18nKey: "newMembers",
    icon: UserPlusIcon,
    maxSpan: 1,
    Component: DashboardNewMembersWidget,
    isVisible: ({ data }) => data.visibility.showKpis && Boolean(data.overviewLayout.memberStats.newMembers),
  },
  {
    id: DashboardWidgetId.Visitors,
    i18nKey: "visitors",
    icon: UserRoundPlusIcon,
    maxSpan: 1,
    Component: DashboardVisitorsWidget,
    isVisible: ({ data }) => data.visibility.showKpis && Boolean(data.overviewLayout.memberStats.visitors),
  },
  {
    id: DashboardWidgetId.TotalIncome,
    i18nKey: "totalInflow",
    icon: ArrowUpIcon,
    maxSpan: 1,
    Component: DashboardTotalIncomeWidget,
    isVisible: (input) => hasFinanceStat(input, FinanceOverviewCardId.TotalIncome),
  },
  {
    id: DashboardWidgetId.TotalExpenses,
    i18nKey: "totalOutflow",
    icon: ArrowDownIcon,
    maxSpan: 1,
    Component: DashboardTotalExpensesWidget,
    isVisible: (input) => hasFinanceStat(input, FinanceOverviewCardId.TotalExpenses),
  },
  {
    id: DashboardWidgetId.NetBalance,
    i18nKey: "netCashFlow",
    icon: ScaleIcon,
    maxSpan: 1,
    Component: DashboardNetBalanceWidget,
    isVisible: (input) => hasFinanceStat(input, FinanceOverviewCardId.NetBalance),
  },
  {
    id: DashboardWidgetId.TransfersOut,
    i18nKey: "transfersOut",
    icon: ArrowDownIcon,
    maxSpan: 1,
    Component: DashboardTransfersOutWidget,
    isVisible: (input) => hasFinanceStat(input, FinanceOverviewCardId.TransfersOut),
  },
  {
    id: DashboardWidgetId.CashOnHand,
    i18nKey: "cashOnHand",
    icon: ScaleIcon,
    maxSpan: 1,
    Component: DashboardCashOnHandWidget,
    isVisible: (input) => hasFinanceStat(input, FinanceOverviewCardId.CashOnHand),
  },
  {
    id: DashboardWidgetId.PendingApprovals,
    i18nKey: "pendingApprovals",
    icon: ClipboardCheckIcon,
    maxSpan: 1,
    Component: DashboardPendingApprovalsWidget,
    isVisible: ({ data }) => data.visibility.showKpis && Boolean(data.overviewLayout.activityStats.pendingApprovals),
  },
  {
    id: DashboardWidgetId.ActiveActivities,
    i18nKey: "activeActivities",
    icon: CalendarCheckIcon,
    maxSpan: 1,
    Component: DashboardActiveActivitiesWidget,
    isVisible: ({ data, overviewMode }) =>
      overviewMode === "branch" && data.visibility.showKpis && Boolean(data.overviewLayout.activityStats.activeActivities),
  },
  {
    id: DashboardWidgetId.TransactionCount,
    i18nKey: "transactionCount",
    icon: ActivityIcon,
    maxSpan: 1,
    Component: DashboardTransactionCountWidget,
    isVisible: ({ data, overviewMode }) =>
      overviewMode === "branch" && data.visibility.showKpis && Boolean(data.overviewLayout.activityStats.transactionCount),
  },
  {
    id: DashboardWidgetId.FinanceTrend,
    i18nKey: "financeTrend",
    icon: LineChartIcon,
    maxSpan: 2,
    expandable: true,
    Component: DashboardFinanceTrendWidget,
    isVisible: ({ data, overviewMode }) =>
      overviewMode === "branch" &&
      Boolean(data.overviewLayout.trendChart && chartHasRenderableData(data.overviewLayout.trendChart)),
  },
  {
    id: DashboardWidgetId.MemberMix,
    i18nKey: "memberMix",
    icon: PieChartIcon,
    maxSpan: 2,
    expandable: true,
    Component: DashboardMemberMixWidget,
    isVisible: ({ data }) => data.visibility.showCharts && data.overviewLayout.memberDistribution.rows.length > 0,
  },
  {
    id: DashboardWidgetId.MemberGrowth,
    i18nKey: "memberGrowth",
    icon: UsersIcon,
    maxSpan: 2,
    expandable: true,
    Component: DashboardMemberGrowthWidget,
    // Not branch-only: `buildParishDashboard` computes the same series across every readable branch, so the
    // organisation view was hiding a chart the backend was already sending. "Are we growing" is the question
    // a parish asks first, and it was the one view that could not answer it.
    isVisible: ({ data }) => data.visibility.showCharts,
  },
  {
    id: DashboardWidgetId.ParishGenderInsights,
    i18nKey: "parishGenderInsights",
    icon: CircleUserRoundIcon,
    maxSpan: 2,
    expandable: true,
    Component: DashboardParishGenderInsightsWidget,
    isVisible: (input) =>
      input.data.visibility.showCharts &&
      isParishOverview(input) &&
      Boolean(input.data.overviewLayout.parishInsights.genderChart?.segments?.length),
  },
  {
    id: DashboardWidgetId.ParishAgeInsights,
    i18nKey: "parishAgeInsights",
    icon: ActivityIcon,
    maxSpan: 2,
    expandable: true,
    Component: DashboardParishAgeInsightsWidget,
    isVisible: (input) =>
      input.data.visibility.showCharts &&
      isParishOverview(input) &&
      Boolean(input.data.overviewLayout.parishInsights.ageChart?.segments?.length),
  },
  {
    id: DashboardWidgetId.ParishBranchDistribution,
    i18nKey: "parishBranchDistribution",
    icon: BarChart3Icon,
    maxSpan: 3,
    expandable: true,
    Component: DashboardParishBranchDistributionWidget,
    isVisible: (input) => {
      const chart = input.data.overviewLayout.parishInsights.branchChart
      return input.data.visibility.showCharts && isParishOverview(input) && Boolean(chart?.series[0] && chart.labels.length > 0)
    },
  },
  {
    id: DashboardWidgetId.RecentRecords,
    i18nKey: "recentRecords",
    icon: FileClockIcon,
    maxSpan: 3,
    expandable: true,
    Component: DashboardRecentRecordsWidget,
    isVisible: ({ data }) => hasDashboardRecentRecords(data.mergedOverview),
    title: (t, { data }) => {
      const table = findDashboardRecentRecordsTable(data.mergedOverview)
      const kind = table ? getDashboardRecentRecordKind(table.id) : "transactions"
      return t(`recentRecords.title.${kind}`)
    },
    subtitle: (t, { data }) => {
      const table = findDashboardRecentRecordsTable(data.mergedOverview)
      const kind = table ? getDashboardRecentRecordKind(table.id) : "transactions"
      return t(`recentRecords.description.${kind}`)
    },
  },
  {
    id: DashboardWidgetId.ScopeNote,
    i18nKey: "scopeNote",
    icon: ActivityIcon,
    maxSpan: 3,
    Component: DashboardScopeNoteWidget,
    isVisible: ({ data }) => Boolean(data.overviewLayout.scopeNote),
  },
]

const DASHBOARD_WIDGETS_BY_ID = new Map(DASHBOARD_WIDGETS.map((widget) => [widget.id, widget]))

function widgetToChrome(t: Translate, widget: DashboardWidgetDef, input: DashboardWidgetRegistryInput): DashboardWidget {
  return {
    id: widget.id,
    title: widget.title ? widget.title(t, input) : t(`widgets.${widget.i18nKey}.title`),
    subtitle: widget.subtitle ? widget.subtitle(t, input) : t(`widgets.${widget.i18nKey}.subtitle`),
    icon: <widget.icon className="size-3.5" aria-hidden="true" />,
  }
}

function visibleWidgets(input: DashboardWidgetRegistryInput): DashboardWidgetDef[] {
  return DASHBOARD_WIDGETS.filter((widget) => widget.isVisible(input))
}

export function buildDashboardWidgetRegistry(t: Translate, input: DashboardWidgetRegistryInput): DashboardWidgetRegistry {
  return Object.fromEntries(
    visibleWidgets(input).map((widget) => [
      widget.id,
      {
        ...widgetToChrome(t, widget, input),
        maxSpan: widget.maxSpan,
        expandable: widget.expandable,
        chrome: widget.chrome,
        render: () => <widget.Component />,
      },
    ]),
  )
}

export function buildInitialDashboardOverviewLayout(t: Translate, input: DashboardWidgetRegistryInput): DashboardLayout {
  const variant = boardVariantOf(input)
  if (variant === DashboardBoardVariant.BranchHome) return buildBranchHomeD4Layout(t, input)
  if (variant === DashboardBoardVariant.Parish && parishTileVisible(input)) {
    return buildD4Layout(t, input, PARISH_HOME_D4_WIDGET_SEEDS, PARISH_HOME_D4_SPANS)
  }
  const seeds = variant === DashboardBoardVariant.Parish ? PARISH_DASHBOARD_WIDGET_SEEDS : BRANCH_DASHBOARD_WIDGET_SEEDS
  const visible = new Set(visibleWidgets(input).map((widget) => widget.id))
  const order: string[] = []
  const widgets: Record<string, DashboardWidget> = {}

  for (const id of seeds) {
    const widget = DASHBOARD_WIDGETS_BY_ID.get(id)
    if (!widget || !visible.has(id)) continue
    order.push(id)
    widgets[id] = widgetToChrome(t, widget, input)
  }

  for (const widget of visibleWidgets(input)) {
    if (widgets[widget.id]) continue
    order.push(widget.id)
    widgets[widget.id] = widgetToChrome(t, widget, input)
  }

  return { order, widgets, spans: {} }
}

/**
 * The branch home default: exactly D4. The D4 tiles the reader may see are placed in design order with the
 * treasury two tracks wide; every other visible widget (the older KPI tiles and charts) is registered but
 * HIDDEN, so it is offered under "Add widgets" and nothing that existed before is lost.
 */
function buildBranchHomeD4Layout(t: Translate, input: DashboardWidgetRegistryInput): DashboardLayout {
  return buildD4Layout(t, input, BRANCH_HOME_D4_WIDGET_SEEDS, BRANCH_HOME_D4_SPANS)
}

/**
 * A D4 default (branch home or parish): the D4 tiles the reader may see in design order with their spans; every
 * other visible widget is registered but HIDDEN, offered under "Add widgets".
 */
function buildD4Layout(
  t: Translate,
  input: DashboardWidgetRegistryInput,
  seeds: readonly DashboardWidgetId[],
  defaultSpans: Record<string, DashboardWidgetSpan>,
): DashboardLayout {
  const visible = visibleWidgets(input)
  const visibleIds = new Set(visible.map((widget) => widget.id))
  const order = seeds.filter((id) => visibleIds.has(id))
  const widgets: Record<string, DashboardWidget> = {}
  for (const widget of visible) widgets[widget.id] = widgetToChrome(t, widget, input)
  const spans: Record<string, DashboardWidgetSpan> = {}
  for (const [id, span] of Object.entries(defaultSpans)) if (visibleIds.has(id as DashboardWidgetId)) spans[id] = span
  return { order, widgets, spans }
}
